<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-credit-card me-2"></i>Supplier Payments Management
      </h4>
      <div class="d-flex">
        <button v-if="profile.role !== 'user'" class="btn btn-outline-primary me-2" @click="openAddPaymentModal">
          <i class="bi bi-plus-circle me-1"></i> Add Payment
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading payments...</p>
      </div>

      <!-- Payments Content -->
      <div v-if="!loading" class="payments-content fade-in">
        <!-- Payments Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ payments.total }}</span>
            <span class="text-muted">Payments</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search payment" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Payments Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>CODE</th>
                  <th>SUPPLIER</th>
                  <th>PURCHASE</th>
                  <th>AMOUNT</th>
                  <th>METHOD</th>
                  <th>DUE DATE</th>
                  <th>PAYMENT DATE</th>
                  <th>STATUS</th>
                  <th class="text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(payment, index) in payments.data" :key="payment.id" class="payment-row" :class="{'bg-light': index % 2 === 0}">
                  <td>{{ payment.id.split("-").pop() }}</td>
                  <td>{{ payment.code }}</td>
                  <td>
                    <a v-if="payment.supplier" :href="`/supplier/${payment.supplier.id}`" class="text-decoration-none">{{ payment.supplier.name }}</a>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <a v-if="payment.purchase" :href="`/purchase/${payment.purchase.id}`" class="text-decoration-none">{{ payment.purchase.code }}</a>
                    <span v-else>-</span>
                  </td>
                  <td>{{ formatNumber(payment.amount) }}</td>
                  <td>
                    <span class="badge" :class="getPaymentMethodBadge(payment.payment_method)">{{ formatPaymentMethod(payment.payment_method) }}</span>
                  </td>
                  <td>{{ formatDate(payment.due_date) }}</td>
                  <td>{{ payment.payment_date ? formatDate(payment.payment_date) : '-' }}</td>
                  <td>
                    <Badge :type="getPaymentStatusBadge(payment.status)">{{ payment.status }}</Badge>
                  </td>
                  <td class="text-end">
                    <div class="btn-group">
                      <button v-if="payment.status === 'pending'" class="btn btn-sm btn-outline-success" @click="handleApprovePayment(payment)">
                        <i class="bi bi-check-circle"></i> Approve
                      </button>
                      <button v-if="profile.role !== 'user' && payment.status === 'pending'" class="btn btn-sm btn-outline-primary ms-1" @click="editPayment(payment)">
                        <i class="bi bi-pencil"></i> Edit
                      </button>
                      <button v-if="profile.role !== 'user' && payment.status === 'pending'" class="btn btn-sm btn-outline-danger ms-1" @click="deletePayment(payment.id)">
                        <i class="bi bi-trash"></i> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>
  </Card>

  <AddPaymentModal
    :open="paymentModal"
    :mode="paymentMode"
    :payment="payment"
    :purchases="purchases"
    :suppliers="suppliers"
    @closed="closePaymentModal"
    @submit="handlePaymentSubmit"
  />
</template>

<script>
import supplierPaymentService from "@/api/supplierPaymentService";
import supplierService from "@/api/supplierService";
import formatDate from "@/utils/formatDate";
import formatNumber from "@/utils/formatNumber";
import {decrypt} from "@/utils/crypto";
import confirmDialog from "@/utils/confirmDialog";
import notify from "@/utils/notify";
import Badge from "@/components/badge/Badge";
import Pagination from "@/components/pagination/Pagination.vue";
import AddPaymentModal from "@/components/modal/AddPaymentModal.vue";

export default {
  name: "SupplierPayments",
  components: {
    Badge,
    Pagination,
    AddPaymentModal
  },

  data() {
    return {
      profile: {},
      loading: true,
      payments: {
        data: [],
        total: 0
      },
      suppliers: [],
      purchases: [],
      paymentModal: false,
      paymentMode: 'add',
      payment: {
        organization_id: '',
        purchase_id: '',
        supplier_id: '',
        user_id: '',
        code: '',
        amount: '',
        payment_method: '',
        reference: '',
        status: 'pending',
        due_date: '',
        payment_date: null,
        notes: ''
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
    }
  },

  computed: {
    totalPages() {
      return Math.ceil(this.payments.total / this.dataTablePerPage);
    }
  },

  methods: {
    formatNumber,
    formatDate,
    confirmDialog,
    async getPayments() {
      try {
        this.loading = true;
        const params = {
          page: this.dataTablePage,
          per_page: this.dataTablePerPage,
          search: this.dataTableSearch
        };
        const response = await supplierPaymentService.getSupplierPayments(params);
        this.payments = response || { data: [], total: 0 };
      } catch (error) {
        console.error("Error fetching payments:", error);
        this.payments = { data: [], total: 0 };
      } finally {
        this.loading = false;
      }
    },
    async getSuppliers() {
      try {
        const response = await supplierService.getSuppliers({ per_page: 1000 });
        this.suppliers = response?.data || [];
      } catch (error) {
        console.error("Error fetching suppliers:", error);
        this.suppliers = [];
      }
    },
    formatPaymentMethod(method) {
      return method ? method.replace('_', ' ').toUpperCase() : '-';
    },
    getPaymentMethodBadge(method) {
      switch (method) {
        case 'Bank Transfer':
          return 'bg-primary';
        case 'Mpesa':
          return 'bg-success';
        case 'Cheque':
          return 'bg-warning';
        case 'Credit':
          return 'bg-info';
        case 'Cash':
          return 'bg-secondary';
        default:
          return 'bg-secondary';
      }
    },
    getPaymentStatusBadge(status) {
      switch (status) {
        case 'completed':
          return 'success';
        case 'pending':
          return 'warning';
        case 'cancelled':
          return 'danger';
        default:
          return 'secondary';
      }
    },
    openAddPaymentModal() {
      this.paymentMode = 'add';
      this.payment = {
        organization_id: '',
        purchase_id: '',
        supplier_id: '',
        user_id: '',
        code: '',
        amount: '',
        payment_method: '',
        reference: '',
        status: 'pending',
        due_date: '',
        payment_date: null,
        notes: ''
      };
      this.paymentModal = true;
    },
    closePaymentModal() {
      this.paymentModal = false;
      this.paymentMode = 'add';
      this.payment = {
        organization_id: '',
        purchase_id: '',
        supplier_id: '',
        user_id: '',
        code: '',
        amount: '',
        payment_method: '',
        reference: '',
        status: 'pending',
        due_date: '',
        payment_date: null,
        notes: ''
      };
    },
    editPayment(payment) {
      this.payment = {...payment};
      this.paymentMode = 'edit';
      this.paymentModal = true;
    },
    async handlePaymentSubmit(paymentData) {
      if (this.paymentMode === 'add') {
        await this.createPayment(paymentData);
      } else {
        await this.updatePayment(paymentData);
      }
    },
    async createPayment(paymentData) {
      const response = await supplierPaymentService.createSupplierPayment(paymentData);
      if (response) {
        this.closePaymentModal();
        await this.getPayments();
      }
    },
    async updatePayment(paymentData) {
      const response = await supplierPaymentService.updateSupplierPayment(paymentData, paymentData.id);
      if (response) {
        this.closePaymentModal();
        await this.getPayments();
      }
    },
    handleApprovePayment(payment) {
      if (!payment.reference || payment.reference.trim() === '') {
        notify.alert.info('Add the payment reference');
        return;
      }
      confirmDialog('Approve Payment', () => this.approvePayment(payment.id), 'Are you sure you want to approve this payment?');
    },
    async approvePayment(id) {
      const response = await supplierPaymentService.approveSupplierPayment(id);
      if (response) {
        await this.getPayments();
      }
    },
    async deletePayment(id) {
      if (confirm('Are you sure you want to delete this payment?')) {
        const response = await supplierPaymentService.deleteSupplierPayment(id);
        if (response) {
          await this.getPayments();
        }
      }
    },
    onSearchClick() {
      this.dataTablePage = 1;
      this.updateRouteQuery();
      this.getPayments();
    },
    onClearSearchClick() {
      this.dataTableSearch = '';
      this.dataTablePage = 1;
      this.updateRouteQuery();
      this.getPayments();
    },
    updateRouteQuery() {
      this.$router.push({
        query: {
          page: this.dataTablePage,
          per_page: this.dataTablePerPage,
          search: this.dataTableSearch
        }
      });
    }
  },

  watch: {
    dataTablePage() {
      this.updateRouteQuery();
      this.getPayments();
    },
    dataTablePerPage() {
      this.dataTablePage = 1;
      this.updateRouteQuery();
      this.getPayments();
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    await this.getSuppliers();
    await this.getPayments();
  }
}
</script>

<style scoped>
/* Payments page styles */
.payments-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.payment-row {
  transition: all 0.2s ease;
}

.payment-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
